import React from 'react';
import { Star, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { testimonials, businessInfo } from '../data/mock';

const Testimonials = () => {
  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 to-teal/5 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold text-navy mb-6">
              Client Testimonials
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Don't just take our word for it. Here's what our clients have to say about working with Wonderman Bookkeeping.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border border-slate-200 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  {/* Stars */}
                  <div className="flex gap-1 mb-6">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-6 h-6 text-amber fill-amber" />
                    ))}
                  </div>
                  
                  {/* Quote */}
                  <blockquote className="text-lg text-slate-700 italic mb-8 leading-relaxed">
                    "{testimonial.text}"
                  </blockquote>
                  
                  {/* Author */}
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-teal/20 flex items-center justify-center">
                      <span className="text-xl font-bold text-teal">
                        {testimonial.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-navy">{testimonial.name}</p>
                      <p className="text-sm text-slate-500">{testimonial.business}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Trustpilot CTA */}
          <div className="mt-16 text-center">
            <Card className="border-0 bg-slate-50 max-w-2xl mx-auto">
              <CardContent className="p-10">
                <div className="flex justify-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-8 h-8 text-amber fill-amber" />
                  ))}
                </div>
                <h3 className="text-2xl font-bold text-navy mb-4">
                  See More Reviews on Trustpilot
                </h3>
                <p className="text-slate-600 mb-6">
                  We're proud of our reputation. Visit our Trustpilot page to read more reviews from satisfied clients.
                </p>
                <Button 
                  variant="outline" 
                  className="border-2 border-teal text-teal hover:bg-teal hover:text-white"
                  disabled
                >
                  <ExternalLink className="mr-2" size={18} />
                  Trustpilot Link Coming Soon
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-teal/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-navy mb-6">
            Ready to Experience the Difference?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Join our satisfied clients and take the stress out of your bookkeeping. 
            Get in touch for a free, no-obligation consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${businessInfo.phone}`}>
              <Button size="lg" className="bg-amber hover:bg-amber-dark text-navy font-semibold px-8 h-14 text-lg w-full sm:w-auto">
                Call: {businessInfo.phone}
              </Button>
            </a>
            <a href={`mailto:${businessInfo.email}?subject=Bookkeeping%20Enquiry&body=Hello%20Wonderman%20Bookkeeping%2C%0A%0AI%20am%20interested%20in%20learning%20more%20about%20your%20bookkeeping%20services.%0A%0AName%3A%20%0ABusiness%20Name%3A%20%0APhone%3A%20%0A%0APlease%20get%20in%20touch%20at%20your%20earliest%20convenience.%0A%0AThank%20you.`}>
              <Button size="lg" variant="outline" className="border-2 border-navy text-navy hover:bg-navy hover:text-white px-8 h-14 text-lg w-full sm:w-auto">
                Send a Message
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Testimonials;
